'use client';

import { useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { getStoredStoreId, setStoredStoreId } from '@/lib/store-storage';
import { getCookie } from '@/lib/cookies';

const ENTRY_PREFERENCES_KEY = 'dank_entry_preferences';
const STORAGE_EXPIRY_DAYS = 30;

interface StoredPreferences {
  ageVerified: boolean;
  shoppingMethod: 'pickup' | 'delivery';
  selectedLocationId?: number;
  postalCode?: string;
  expiryDate: number;
}

export default function HomePageRedirect() {
  const { setStore, setFulfillmentMode, state } = useCart();

  useEffect(() => {
    // Only redirect if we're on the homepage and don't have a store selected
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== '/') return;
    
    // Wait a bit for cart context to initialize
    const timer = setTimeout(() => {
      // If we already have a store selected in cart context, don't redirect
      if (state.storeCovaLocationId) return;

      try {
        // Check for stored entry preferences first (most recent)
        const stored = getCookie(ENTRY_PREFERENCES_KEY);
        if (stored) {
          const prefs: StoredPreferences = JSON.parse(stored);
          
          // Check if preferences are still valid (not expired)
          if (prefs.expiryDate && Date.now() < prefs.expiryDate && prefs.ageVerified) {
            // If we have a selected location, hydrate state
            if (prefs.selectedLocationId) {
              // Set store and fulfillment mode in cart context
              setStore(prefs.selectedLocationId);
              setFulfillmentMode(prefs.shoppingMethod);
              setStoredStoreId(prefs.selectedLocationId);
              return;
            }
          }
        }

        // Also check dedicated store ID storage
        const storedStoreId = getStoredStoreId();
        if (storedStoreId) {
          setStore(storedStoreId);
          return;
        }
        
        // Also check cart storage for store selection
        const cartStorage = localStorage.getItem('dank_client_cart_v2');
        if (cartStorage) {
          try {
            const cartData = JSON.parse(cartStorage);
            if (cartData.storeCovaLocationId) {
              // Set store and fulfillment mode in cart context
              setStore(cartData.storeCovaLocationId);
              setFulfillmentMode(cartData.fulfillmentMode || 'pickup');
              setStoredStoreId(cartData.storeCovaLocationId);
              return;
            }
          } catch (e) {
            // Invalid cart storage, ignore
          }
        }
      } catch (e) {
        // Error reading storage, ignore
        console.error('[HomePageRedirect] Error:', e);
      }
    }, 100); // Small delay to ensure cart context is initialized

    return () => clearTimeout(timer);
  }, [setStore, setFulfillmentMode, state.storeCovaLocationId]);

  return null; // This component doesn't render anything
}

